/* eslint-disable react/destructuring-assignment */
import { useEffect, useState, useMemo } from 'react';
import { Col, Row, Table, Button, Nav, Form } from 'react-bootstrap';
import { useMutation } from 'react-query';
import { StyledTh, StyledTr } from '../../../pages/OnlineStore/styles';
import { useOnlineStore } from '../../../contexts/onlineStoreContext';
import ISaveInvoiceItemDto from '../../../dtos/ISaveInvoiceItemDto';
import NumericInput from '../../NumericInput';
import { ReactComponent as EraserIcon } from '../../../assets/icons/eraser-solid.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ReactComponent as ReactArrowRight } from '../../../assets/icons/timeline/arrow_right.svg';
import InvoiceHttpService from '../../../services/http/invoice-http';
import { InvoiceStatus } from '../../../constants/invoiceStatus';
import { formatCurrency } from '../../../utils/strings';
import { ProductAvailability } from '../../../constants/productAvailability';
import { useStructureCalculation } from '../../../contexts/structureCalculationContext';
import StructureCalculationModal from '../StructureCalculation';
import useWindowSize from '../../../hooks/useWindowSize';
import {
    steps as generatorSteps,
    readyKitsSteps,
} from '../../../constants/onlineStore';
import { StyledSquare, StyledTitleNav } from '../Base/styles';
import StyledKitPowerInfo from './styles';
import ConfirmationDialog from '../../ConfirmationDialog';
import { NotificationIcon } from '../../../constants/notificationIcon';
import BaseAvailabilityBadge from '../../BaseAvailabilityBadge';
import { replaceItemAtIndex } from '../../../utils/replace-array';
import CartOrInvoiceProductNumericInput from '../InvoiceProductNumericInput/components/cart-or-invoice-product-numeric-input';
import handleResponseError from '../../../utils/handleResponseError';
import IInvoiceItem from '../../../shared/interfaces/invoice-item.interface';
import CartTableCell from './Cell/cart-table-cell';
import IUpdateInvoicePriceDto from '../../../dtos/IUpdateInvoicePriceDto';
import { useAuth } from '../../../contexts/authContext';
import ConfigurationReference from '../../../constants/configurationReference';
import ConfigurationHttpService from '../../../services/http/configuration-http';

const kitPowerDecimals = 2;
interface CartProps {
    onBudgetEditing?: boolean;
    budget?: boolean;
    disabled?: boolean;
    invoice?: any;
    step?: any;
    paymentDeliveryEditing?: boolean;
    setBudget?: (value: any) => void;
    setStep?: (value: any) => void;
    addToInvoiceItems?: (value: any) => void;
}

const Cart = (props: CartProps) => {
    const {
        cart,
        setCart,
        step,
        setStep,
        setGroupId,
        productType,
        setProducts,
        setReload,
        setLoadingCart,
        loadingCart,
        engineeringInsuranceActived,
        setEngineeringInsuranceActived
    } = useOnlineStore();

    const { user } = useAuth();

    const { setStructureCalculationValues, setLines } =
        useStructureCalculation();
    const [calculationOfStructuresModal, setCalculationOfStructuresModal] =
        useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [invoice, setInvoice] = useState(props.invoice);
    const [items, setItems] = useState(props.invoice?.invoiceItems ?? []);
    const [firstLoad, setFirstLoad] = useState(true);
    const [engineeringInsurancePercentage, setEngineeringInsurancePercentage] = 
        useState();
    const [actualInsurancePercentage, setActualInsurancePercentage] = useState();

    const size = useWindowSize();
    const [steps, setSteps] = useState([]);

    const nextStep = (item: any) => {
        if (
            props.invoice.invoiceItems &&
            props.invoice.invoiceItems.length > 0
        ) {
            props.setStep(item.number);
        } else if (item.number > 2) {
            props.setStep(props.step);
        } else {
            props.setStep(item.number);
        }
    };

    const mutationUpdateInvoicePrice = useMutation({
        mutationKey: ['update-invoice-price', invoice?.id],
        mutationFn: async (params: IUpdateInvoicePriceDto) => {
            setLoadingCart(true);

            return InvoiceHttpService.updatePrice(props.invoice.id, params);
        },
        onError: (error: any) => {
            handleResponseError(
                error,
                'Ocorreu um erro ao atualizar valores do pedido',
            );
        },
        onSuccess: ({ data }) => {
            console.log(data);
            Object.assign(props.invoice, {
                rawValue: data.rawValue,
                subTotal: data.subTotal,
                percentage: data.percentage,
                totalInvoice: data.totalInvoice,
                marginLiquid: data.marginLiquid,
                discountValue: data.discountValue,
                netCommission: data.netCommission,
                interestValue: data.interestValue,
                grossCommission: data.grossCommission,
                subTotalWithDiscount: data.subTotalWithDiscount,
                engineeringInsuranceValue: data.engineeringInsuranceValue
            });
        },
        onSettled: () => {
            setLoadingCart(false);
        },
    });

    const mutationClearCart = useMutation(
        async () => InvoiceHttpService.emptyCart(),
        {
            onError: (error: any) => {
                handleResponseError(
                    error,
                    'Ocorreu um erro ao limpar o carrinho.',
                );
            },
            onSuccess: (response: any) => {
                if (props.onBudgetEditing) {
                    props.setBudget(response.data);
                } else {
                    setCart(response.data);
                }

                if (productType === 'PA' || productType === 'BF') {
                    setStep(2);
                    setGroupId('');
                }

                if (productType === 'MP') {
                    setGroupId('1001');
                    setStep(1);
                }

                setProducts([]);
                setStructureCalculationValues([]);
                setLines([]);
            },
        },
    );

    async function updateEngineeringInsurance(actived: boolean): Promise<void> {
        setLoadingCart(true);
        try {
            await InvoiceHttpService.updateInsurance(
                invoice.id,
                {
                    engineeringInsurance: actived
                }
            );

            // eslint-disable-next-line no-param-reassign
            props.invoice.engineeringInsuranceIsActive = actived;
            
            setEngineeringInsuranceActived(actived); 
            mutationUpdateInvoicePrice.mutate(null);
        } finally {
            setLoadingCart(false);
        }
    }

    const getActualConfig = async () => {
        try {
            const response = await ConfigurationHttpService.readOne(
                ConfigurationReference.EngineeringInsurancePercentage
            );

            setActualInsurancePercentage(response.data.value);
        } catch (error) {
            handleResponseError(
                error,
                'Ocorreu um erro ao buscar a porcentagem vigente do pedido.',
            );
        }
    };

    useEffect(() => {
        if (props.invoice && props.invoice.id) {
            setInvoice(props.invoice);
            if (props.invoice.status === InvoiceStatus.Cart && firstLoad) {
                setCart({ ...props.invoice, load: true });
                setFirstLoad(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.invoice]);

    useEffect(() => {
        if (props.onBudgetEditing) {
            props.setBudget(props.invoice);
        } else {
            setCart(props.invoice);
        }

        getActualConfig();

        setEngineeringInsuranceActived(props.invoice.engineeringInsuranceIsActive);

        setItems(props.invoice?.invoiceItems ?? []);

        const generators = generatorSteps.filter(
            (item: any) => item.number !== 1 && item.number !== 4,
        );
        const kits = readyKitsSteps.filter((item: any) => item.number !== 4);

        setSteps(props.invoice.type === 'MP' ? generators : kits);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setItems(invoice?.invoiceItems ?? []);
    }, [invoice?.invoiceItems]);

    const filteredItems = useMemo(() => {
        return items?.filter((item: IInvoiceItem) => item.quantity > 0) ?? [];
    }, [items]);

    const memoizedSteps = useMemo(() => {
        return props.onBudgetEditing ? steps : [];
    }, [props.onBudgetEditing, steps]);

    return (
        <>
            <>
                {invoice?.status &&
                    (invoice?.status === InvoiceStatus.Cart ||
                        invoice?.status === InvoiceStatus.Budget) && (
                        <Col>
                            <Row className="align-items-center">
                                {props.onBudgetEditing && (
                                    <Row>
                                        {memoizedSteps.map(
                                            (item: any, index: any) => (
                                                <div key={index}>
                                                    <Nav.Item>
                                                        <Nav.Link
                                                            style={
                                                                props.step ===
                                                                item.number
                                                                    ? {
                                                                          color: '#F2994A',
                                                                          fontWeight: 600,
                                                                      }
                                                                    : {
                                                                          color: 'rgba(112, 112, 112, 0.3)',
                                                                      }
                                                            }
                                                            eventKey={
                                                                item.number
                                                            }
                                                            onClick={async () => {
                                                                nextStep(item);
                                                            }}
                                                        >
                                                            <Row>
                                                                <Col xl={1}>
                                                                    <StyledSquare
                                                                        background={
                                                                            props.step ===
                                                                            item.number
                                                                                ? '#F2994A'
                                                                                : '#fbf3e6'
                                                                        }
                                                                    >
                                                                        <h5
                                                                            style={{
                                                                                fontSize: 38,
                                                                                fontWeight: 600,
                                                                                color:
                                                                                    props.step ===
                                                                                    item.number
                                                                                        ? '#F2994A'
                                                                                        : 'rgba(112, 112, 112, 0.3)',
                                                                            }}
                                                                        >
                                                                            {index +
                                                                                1}
                                                                        </h5>
                                                                    </StyledSquare>
                                                                </Col>

                                                                <Col className="my-auto">
                                                                    <StyledTitleNav
                                                                        className={
                                                                            size.width <
                                                                                1500 &&
                                                                            invoice.type ===
                                                                                'MP'
                                                                                ? 'mr-n4'
                                                                                : 'ml-3'
                                                                        }
                                                                        color={
                                                                            props.step ===
                                                                            item.number
                                                                                ? '#F2994A'
                                                                                : 'rgba(112, 112, 112, 0.3)'
                                                                        }
                                                                    >
                                                                        {
                                                                            item.title
                                                                        }
                                                                    </StyledTitleNav>
                                                                </Col>
                                                                {item.number !==
                                                                    3 && (
                                                                    <ReactArrowRight
                                                                        style={{
                                                                            position:
                                                                                'relative',
                                                                            top: '-3px',
                                                                        }}
                                                                        className={`my-auto ${
                                                                            size.width <
                                                                                1500 &&
                                                                            invoice.type ===
                                                                                'MP'
                                                                                ? ''
                                                                                : 'mr-4'
                                                                        }  `}
                                                                        fill={
                                                                            props.step ===
                                                                            item.number
                                                                                ? '#F2994A'
                                                                                : 'rgba(112, 112, 112, 0.3)'
                                                                        }
                                                                    />
                                                                )}
                                                            </Row>
                                                        </Nav.Link>
                                                    </Nav.Item>
                                                </div>
                                            ),
                                        )}
                                    </Row>
                                )}
                                <Col className="text-right">
                                    {!!cart &&
                                        !!cart?.invoiceItems?.length &&
                                        cart?.type === 'MP' &&
                                        ((props?.onBudgetEditing &&
                                            cart?.status ===
                                                InvoiceStatus.Budget) ||
                                            (step === 3 &&
                                                cart.status ===
                                                    InvoiceStatus.Cart)) && (
                                            <>
                                                <Button
                                                    size="lg"
                                                    variant="outline"
                                                    style={{
                                                        color: '#2F80ED',
                                                    }}
                                                    className="px-0"
                                                    onClick={() =>
                                                        setCalculationOfStructuresModal(
                                                            true,
                                                        )
                                                    }
                                                >
                                                    <EditIcon
                                                        fill="#2F80ED"
                                                        className="mr-2"
                                                        width={20}
                                                    />
                                                    Editar estrutura de fixação
                                                </Button>
                                            </>
                                        )}
                                    {(!!cart || !!invoice) &&
                                        (!!cart?.invoiceItems?.length ||
                                            !!invoice?.invoiceItems?.length) &&
                                        !props.paymentDeliveryEditing &&
                                        !props.onBudgetEditing &&
                                        invoice?.status ===
                                            InvoiceStatus.Cart && (
                                            <Button
                                                variant="outline"
                                                size="lg"
                                                style={{
                                                    color: '#2F80ED',
                                                }}
                                                className="px-0 ml-4"
                                                onClick={() => {
                                                    setShowConfirmation(true);
                                                    setReload(true);
                                                }}
                                            >
                                                <EraserIcon
                                                    fill="#2F80ED"
                                                    className="mr-2"
                                                    width={20}
                                                />
                                                Limpar Carrinho
                                            </Button>
                                        )}
                                </Col>
                            </Row>
                        </Col>
                    )}
                {invoice?.status !== InvoiceStatus.Cart &&
                    invoice?.status !== InvoiceStatus.Budget && <br />}

                {invoice?.kitPower > 0 && (
                    <StyledKitPowerInfo>
                        {`Potência do kit: ${invoice?.kitPower?.toFixed(
                            kitPowerDecimals,
                        )}kW`}
                    </StyledKitPowerInfo>
                )}
                <Table
                    hover
                    bordered
                    className="text-center mt-2"
                    size="sm"
                    style={{ backgroundColor: '#ffffff' }}
                >
                    <thead>
                        <tr>
                            <StyledTh>EQUIPAMENTOS</StyledTh>
                            <StyledTh>CÓDIGO</StyledTh>
                            <StyledTh>DESCRIÇÃO</StyledTh>
                            <StyledTh>DISPONIBILIDADE</StyledTh>
                            <StyledTh>QUANTIDADE</StyledTh>
                            {user?.canViewProductPrices && (
                                <StyledTh>VALOR UNITÁRIO</StyledTh>
                            )}
                            {user?.canViewProductPrices && (
                                <StyledTh>VALOR TOTAL</StyledTh>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredItems?.map((item: IInvoiceItem) => (
                            <tr key={`${item.id}-${item.productErpCode}`}>
                                <td>{item.groupName}</td>
                                <td>{item.productErpCode}</td>
                                <td>{item.productName}</td>
                                <td>
                                    <BaseAvailabilityBadge
                                        isImmediate={
                                            item.availability ===
                                            ProductAvailability.Immediate
                                        }
                                        boardingDate={item.availability}
                                    />
                                </td>
                                <td>
                                    {props.onBudgetEditing ? (
                                        <NumericInput
                                            disabled={
                                                props.onBudgetEditing ===
                                                undefined
                                                    ? props.disabled
                                                    : !props.onBudgetEditing
                                            }
                                            tableItem={item ?? {}}
                                            onBudgetEditing={
                                                props.onBudgetEditing
                                            }
                                            invoice={invoice ?? {}}
                                            setInvoice={
                                                props.onBudgetEditing
                                                    ? props.setBudget
                                                    : setCart
                                            }
                                            item={
                                                {
                                                    status: invoice.status,
                                                    invoiceId: item.invoiceId,
                                                    id: item.id,
                                                    productErpCode:
                                                        item.productErpCode,
                                                    groupCode: item?.groupCode,
                                                    groupName: item?.groupName,
                                                    productName:
                                                        item.productName,
                                                    unitValue: item.unitValue,
                                                    isCart: false,
                                                    availability:
                                                        item?.boardingDate,
                                                    unitPower: item?.unitPower,
                                                } as ISaveInvoiceItemDto
                                            }
                                        />
                                    ) : (
                                        <CartOrInvoiceProductNumericInput
                                            disabled={
                                                props.onBudgetEditing ===
                                                undefined
                                                    ? props.disabled
                                                    : !props.onBudgetEditing
                                            }
                                            setAllItens={setItems}
                                            allItens={
                                                items ?? []
                                            }
                                            invoiceId={invoice?.id ?? 0}
                                            type={
                                                // eslint-disable-next-line no-nested-ternary
                                                invoice?.status ===
                                                InvoiceStatus.Cart
                                                    ? 'CART'
                                                    : [
                                                          InvoiceStatus.Budget,
                                                          InvoiceStatus.BudgetExpired,
                                                      ].includes(
                                                          invoice?.status,
                                                      )
                                                    ? 'BUDGET'
                                                    : 'INVOICE'
                                            }
                                            localItem={{
                                                B1_COD: item.productErpCode,
                                                B1_DESC: item.productName,
                                                BM_GRUPO: item.groupCode,
                                                BM_DESC: item.groupName,
                                                boardingDate: item.availability,
                                                VLR_TOT: item.unitValue,
                                                CAT_DESC: item.productCategory,
                                                power: item.unitPower,
                                            }}
                                            onChange={(dataItem) => {
                                                setItems((valueData: any[]) => {
                                                    const itemToUpdateIndex =
                                                        valueData.findIndex(
                                                            (value) =>
                                                                value.productErpCode ===
                                                                dataItem.B1_COD,
                                                        );
                                                    if (
                                                        itemToUpdateIndex !== -1
                                                    ) {
                                                        return replaceItemAtIndex(
                                                            valueData,
                                                            itemToUpdateIndex,
                                                            {
                                                                ...valueData[
                                                                    itemToUpdateIndex
                                                                ],
                                                                quantity:
                                                                    dataItem.quantity,
                                                                totalValue:
                                                                    dataItem.totalValue,
                                                            },
                                                        );
                                                    }
                                                    return valueData;
                                                });
                                            }}
                                            productType={
                                                invoice?.type ?? productType
                                            }
                                            updateCartOrInvoiceData={(data) => {
                                                setInvoice(data);
                                                if (props.onBudgetEditing) {
                                                    props.setBudget(data);
                                                } else {
                                                    setCart(data);
                                                }
                                            }}
                                            quantityValue={item.quantity}
                                            setLoading={(loading) => {
                                                setLoadingCart(loading);
                                            }}
                                        />
                                    )}
                                </td>
                                {user?.canViewProductPrices && (
                                    <>
                                        <td>
                                            {formatCurrency(item.unitValue)}
                                        </td>
                                        <td>
                                            {formatCurrency(item.totalValue)}
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <StyledTr>
                            <td
                                className="text-right"
                                colSpan={user?.canViewProductPrices ? 5 : 3}
                            >
                                Sub Total
                            </td>
                            <td>
                                <CartTableCell
                                    value={props.invoice?.subTotal?.toString()}
                                    format="currency"
                                    editable={false}
                                    loading={loadingCart}
                                />
                            </td>
                            <td>-</td>
                        </StyledTr>

                        {(props.paymentDeliveryEditing ||
                            (invoice?.status &&
                                invoice?.status !== InvoiceStatus.Cart)) && (
                            <>
                                <StyledTr>
                                    <td
                                        className="text-right"
                                        colSpan={
                                            user?.canViewProductPrices ? 5 : 3
                                        }
                                    >
                                        Desconto
                                    </td>
                                    <td>
                                        <CartTableCell
                                            value={props.invoice?.discountValue?.toString()}
                                            format="currency"
                                            loading={loadingCart}
                                            editable={
                                                [
                                                    InvoiceStatus.Invoice,
                                                    InvoiceStatus.Cart,
                                                    InvoiceStatus.Budget,
                                                ].includes(
                                                    props?.invoice?.status,
                                                ) &&
                                                !loadingCart
                                            }
                                            onSubmit={(newValue: number) =>
                                                mutationUpdateInvoicePrice.mutate(
                                                    {
                                                        discountValue: newValue,
                                                    },
                                                )
                                            }
                                            maxValue={props.invoice?.subTotal}
                                        />
                                    </td>
                                    <td>
                                        <CartTableCell
                                            value={
                                                props.invoice?.percentage ===
                                                null
                                                    ? '0'
                                                    : props.invoice?.percentage?.toString()
                                            }
                                            format="percent"
                                            loading={loadingCart}
                                            maxValue={100}
                                            editable={
                                                [
                                                    InvoiceStatus.Invoice,
                                                    InvoiceStatus.Cart,
                                                    InvoiceStatus.Budget,
                                                ].includes(
                                                    props?.invoice?.status,
                                                ) &&
                                                !loadingCart
                                            }
                                            onSubmit={(newValue: number) =>
                                                mutationUpdateInvoicePrice.mutate(
                                                    {
                                                        discountPercentage:
                                                            newValue,
                                                    },
                                                )
                                            }
                                        />
                                    </td>
                                </StyledTr>
                                <StyledTr>
                                    <td
                                        className="text-right"
                                        colSpan={
                                            user?.canViewProductPrices ? 5 : 3
                                        }
                                    >
                                        Total
                                    </td>
                                    <td>
                                        <CartTableCell
                                            value={
                                                props.invoice
                                                    ?.subTotalWithDiscount ===
                                                null
                                                    ? '0'
                                                    : props.invoice?.subTotalWithDiscount?.toString()
                                            }
                                            format="currency"
                                            editable={false}
                                            loading={loadingCart}
                                        />
                                    </td>
                                    <td>-</td>
                                </StyledTr>
                                <StyledTr>
                                    <td
                                        className="text-right"
                                        colSpan={
                                            user?.canViewProductPrices ? 5 : 3
                                        }
                                    >
                                        Acréscimos Cartão
                                    </td>
                                    <td>
                                        <CartTableCell
                                            value={props.invoice?.interestValue?.toString()}
                                            format="currency"
                                            editable={false}
                                            loading={loadingCart}
                                        />
                                    </td>
                                    <td>-</td>
                                </StyledTr>
                                <StyledTr>
                                    <td
                                        className="text-right"
                                        colSpan={
                                            user?.canViewProductPrices ? 5 : 3
                                        }
                                    >
                                        -
                                    </td>
                                    <td>Bruto</td>
                                    <td>Liquido</td>
                                </StyledTr>
                                <StyledTr>
                                    <td
                                        className="text-right"
                                        colSpan={
                                            user?.canViewProductPrices ? 5 : 3
                                        }
                                    >
                                        Serviço
                                    </td>
                                    <td>
                                        <CartTableCell
                                            value={props.invoice?.grossCommission?.toString()}
                                            format="currency"
                                            loading={loadingCart}
                                            editable={
                                                !props.onBudgetEditing &&
                                                [
                                                    InvoiceStatus.Invoice,
                                                    InvoiceStatus.Cart,
                                                    InvoiceStatus.Budget,
                                                ].includes(
                                                    props?.invoice?.status,
                                                ) &&
                                                !loadingCart
                                            }
                                            onSubmit={(newValue: any) =>
                                                mutationUpdateInvoicePrice.mutate(
                                                    {
                                                        grossCommission:
                                                            newValue,
                                                    },
                                                )
                                            }
                                        />
                                    </td>
                                    <td>
                                        <CartTableCell
                                            value={
                                                props.invoice?.netCommission ===
                                                null
                                                    ? '0'
                                                    : props.invoice?.netCommission?.toString()
                                            }
                                            format="currency"
                                            editable={false}
                                            loading={loadingCart}
                                        />
                                    </td>
                                </StyledTr>
                            </>
                        )}

                        { 
                            (
                                Number(invoice.engineeringInsurancePercentage) > 0 
                                || (
                                    ['CART', 'BUDGET'].includes(invoice.status) 
                                    && actualInsurancePercentage
                                    && Number(actualInsurancePercentage) > 0
                                )
                            )
                            && 
                                (
                                    props.paymentDeliveryEditing ||
                                    (invoice?.status &&
                                        invoice?.status !== InvoiceStatus.Cart)
                                )
                            &&
                            (
                                <StyledTr>
                                    <td
                                        className="text-right"
                                        colSpan={
                                            user?.canViewProductPrices ? 5 : 3
                                        }
                                    >
                                        Risco Engenharia
                                    </td>
                                    <td>
                                        <CartTableCell
                                            value={
                                                  engineeringInsuranceActived ?
                                                  invoice.engineeringInsuranceValue : 
                                                  '0'
                                            }
                                            format="currency"
                                            editable={false}
                                            loading={loadingCart}
                                        />
                                    </td>
                                    <td>
                                        <Form.Check
                                            id="engineering-insurance-switch"
                                            type="switch"
                                            checked={engineeringInsuranceActived}
                                            onChange={
                                                // eslint-disable-next-line no-undef
                                                (e: React.ChangeEvent<HTMLInputElement>) => updateEngineeringInsurance(e.target.checked)
                                            }
                                            disabled={
                                                loadingCart || (!['CART', 'BUDGET', 'INVOICE'].includes(invoice.status))
                                            }
                                        />
                                    </td>
                                </StyledTr>
                            )
                        }

                        {(props.paymentDeliveryEditing ||
                            (invoice?.status &&
                                invoice?.status !== InvoiceStatus.Cart)) && (
                            <>
                                <StyledTr>
                                    <td
                                        className="text-right"
                                        colSpan={
                                            user?.canViewProductPrices ? 5 : 3
                                        }
                                    >
                                        Total NF
                                    </td>
                                    <td>
                                        <CartTableCell
                                            value={props.invoice?.totalInvoice?.toString()}
                                            format="currency"
                                            editable={false}
                                            loading={loadingCart}
                                        />
                                    </td>
                                    <td>-</td>
                                </StyledTr>
                            </>
                        )}
                    </tfoot>
                </Table>
            </>

            <StructureCalculationModal
                show={calculationOfStructuresModal}
                onHide={() => setCalculationOfStructuresModal(false)}
                budget={props?.budget}
                invoiceId={invoice?.id}
                addToInvoiceItems={props?.addToInvoiceItems}
            />

            {showConfirmation && (
                <ConfirmationDialog
                    show={showConfirmation}
                    onHide={() => setShowConfirmation(false)}
                    onConfirm={mutationClearCart.mutateAsync}
                    title="Limpar carrinho"
                    text="Deseja limpar o carrinho? As informações serão perdidas."
                    icon={NotificationIcon.Warning}
                />
            )}
        </>
    );
};

export default Cart;
